import React, { useState, useEffect, useCallback } from "react";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import { 
  Calendar, Plus, Check, X, FileText, Clock, ChevronDown,
  Loader2, AlertCircle
} from "lucide-react";
import { toast } from "sonner";

export default function LeavePage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("my-leaves");
  const [loading, setLoading] = useState(false);
  const [myRequests, setMyRequests] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [balance, setBalance] = useState(null);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [applyForm, setApplyForm] = useState({
    leave_type: "casual",
    from_date: "",
    to_date: "",
    reason: ""
  });

  const isApprover = ["supervisor", "ci", "admin", "package_head"].includes(user?.role);

  const fetchMyRequests = useCallback(async () => {
    try {
      const res = await api.get("/leave/my-requests");
      setMyRequests(res.data.requests || []);
    } catch (err) {
      console.error("Error fetching requests:", err);
    }
  }, []);

  const fetchPendingApprovals = useCallback(async () => {
    if (!isApprover) return;
    try {
      const res = await api.get("/leave/pending");
      setPendingApprovals(res.data.items || []);
    } catch (err) {
      console.error("Error fetching pending:", err);
    }
  }, [isApprover]);

  const fetchBalance = useCallback(async () => {
    try {
      const res = await api.get("/leave/balance");
      setBalance(res.data);
    } catch (err) {
      console.error("Error fetching balance:", err);
    }
  }, []);

  useEffect(() => {
    fetchMyRequests();
    fetchPendingApprovals();
    fetchBalance();
  }, [fetchMyRequests, fetchPendingApprovals, fetchBalance]);

  const handleApply = async () => {
    if (!applyForm.from_date || !applyForm.to_date || !applyForm.reason) {
      toast.error("Please fill all required fields");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/leave/apply", applyForm);
      toast.success(res.data.message);
      setShowApplyModal(false);
      setApplyForm({ leave_type: "casual", from_date: "", to_date: "", reason: "" });
      fetchMyRequests();
      fetchBalance();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to apply leave");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (leaveId) => {
    if (!confirm("Cancel this leave request?")) return;
    try {
      await api.delete(`/leave/cancel/${leaveId}`);
      toast.success("Leave cancelled");
      fetchMyRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel");
    }
  };

  const handleApprove = async (leaveId) => {
    try {
      await api.post(`/leave/approve/${leaveId}`);
      toast.success("Leave approved");
      fetchPendingApprovals();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to approve");
    }
  };

  const handleReject = async (leaveId) => {
    const reason = prompt("Rejection reason:");
    if (!reason) return;
    try {
      await api.post(`/leave/reject/${leaveId}`, { rejection_reason: reason });
      toast.success("Leave rejected");
      fetchPendingApprovals();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reject");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approved": return "bg-green-100 text-green-700";
      case "rejected": return "bg-red-100 text-red-700";
      case "pending": return "bg-yellow-100 text-yellow-700";
      case "cancelled": return "bg-slate-100 text-slate-500";
      default: return "bg-slate-100 text-slate-600";
    }
  };

  const leaveTypes = [
    { value: "casual", label: "Casual Leave" },
    { value: "sick", label: "Sick Leave" },
    { value: "earned", label: "Earned Leave" },
    { value: "emergency", label: "Emergency Leave" },
    { value: "compensatory", label: "Compensatory Off" }
  ];

  return (
    <div className="space-y-6" data-testid="leave-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Leave Management</h1>
          <p className="text-sm text-slate-500">Apply, track & approve leaves</p>
        </div>
        <button
          onClick={() => setShowApplyModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          data-testid="apply-leave-btn"
        >
          <Plus className="w-4 h-4" />
          Apply Leave
        </button>
      </div>

      {/* Leave Balance */}
      {balance && (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
          <h3 className="font-semibold text-slate-800 mb-3">Leave Balance ({balance.year})</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {Object.entries(balance.balance || {}).map(([type, data]) => (
              <div key={type} className="p-3 bg-slate-50 rounded-lg text-center">
                <div className="text-xl font-bold text-slate-700">{data.remaining}</div>
                <div className="text-xs text-slate-500 capitalize">{type}</div>
                <div className="text-[10px] text-slate-400">{data.used}/{data.quota} used</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-slate-200 pb-2">
        <button
          onClick={() => setActiveTab("my-leaves")}
          className={`px-4 py-2 text-sm font-medium rounded-t transition-colors ${activeTab === "my-leaves" ? "bg-orange-500 text-white" : "text-slate-600 hover:bg-slate-100"}`}
        >
          <FileText className="inline-block w-4 h-4 mr-1" />
          My Leaves
        </button>
        {isApprover && (
          <button
            onClick={() => setActiveTab("approvals")}
            className={`px-4 py-2 text-sm font-medium rounded-t transition-colors ${activeTab === "approvals" ? "bg-orange-500 text-white" : "text-slate-600 hover:bg-slate-100"}`}
          >
            <Clock className="inline-block w-4 h-4 mr-1" />
            Pending Approvals
            {pendingApprovals.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">{pendingApprovals.length}</span>}
          </button>
        )}
      </div>

      {/* My Leaves Tab */}
      {activeTab === "my-leaves" && (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left py-3 px-4">Type</th>
                <th className="text-left py-3 px-4">From</th>
                <th className="text-left py-3 px-4">To</th>
                <th className="text-left py-3 px-4">Days</th>
                <th className="text-left py-3 px-4">Reason</th>
                <th className="text-left py-3 px-4">Status</th>
                <th className="text-left py-3 px-4">Action</th>
              </tr>
            </thead>
            <tbody>
              {myRequests.length > 0 ? myRequests.map((req) => (
                <tr key={req.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-4 capitalize font-medium">{req.leave_type}</td>
                  <td className="py-3 px-4">{req.from_date}</td>
                  <td className="py-3 px-4">{req.to_date}</td>
                  <td className="py-3 px-4">{req.total_days}</td>
                  <td className="py-3 px-4 text-slate-600 max-w-[200px] truncate">{req.reason}</td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-0.5 text-xs font-bold rounded uppercase ${getStatusColor(req.approval_status)}`}>
                      {req.approval_status}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    {req.approval_status === "pending" && (
                      <button
                        onClick={() => handleCancel(req.id)}
                        className="text-xs text-red-500 hover:text-red-700 font-medium"
                      >
                        Cancel
                      </button>
                    )}
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={7} className="text-center py-8 text-slate-400">No leave requests</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pending Approvals Tab */}
      {activeTab === "approvals" && isApprover && (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left py-3 px-4">Employee</th>
                <th className="text-left py-3 px-4">Type</th>
                <th className="text-left py-3 px-4">From</th>
                <th className="text-left py-3 px-4">To</th>
                <th className="text-left py-3 px-4">Days</th>
                <th className="text-left py-3 px-4">Reason</th>
                <th className="text-left py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pendingApprovals.length > 0 ? pendingApprovals.map((req) => (
                <tr key={req.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-4 font-medium">{req.employee_name}</td>
                  <td className="py-3 px-4 capitalize">{req.leave_type}</td>
                  <td className="py-3 px-4">{req.from_date}</td>
                  <td className="py-3 px-4">{req.to_date}</td>
                  <td className="py-3 px-4">{req.total_days}</td>
                  <td className="py-3 px-4 text-slate-600 max-w-[200px] truncate">{req.reason}</td>
                  <td className="py-3 px-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleApprove(req.id)}
                        className="px-3 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleReject(req.id)}
                        className="px-3 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600"
                      >
                        Reject
                      </button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={7} className="text-center py-8 text-slate-400">No pending approvals 🎉</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Apply Leave Modal */}
      {showApplyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md" data-testid="apply-leave-modal">
            <div className="p-4 border-b border-slate-200 flex justify-between items-center">
              <h3 className="font-semibold text-slate-800">Apply for Leave</h3>
              <button onClick={() => setShowApplyModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Leave Type *</label>
                <select
                  value={applyForm.leave_type}
                  onChange={(e) => setApplyForm({ ...applyForm, leave_type: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  data-testid="leave-type-select"
                >
                  {leaveTypes.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">From Date *</label>
                  <input
                    type="date"
                    value={applyForm.from_date}
                    onChange={(e) => setApplyForm({ ...applyForm, from_date: e.target.value })}
                    min={new Date().toISOString().split("T")[0]}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    data-testid="from-date-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">To Date *</label>
                  <input
                    type="date"
                    value={applyForm.to_date}
                    onChange={(e) => setApplyForm({ ...applyForm, to_date: e.target.value })}
                    min={applyForm.from_date || new Date().toISOString().split("T")[0]}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    data-testid="to-date-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Reason *</label>
                <textarea
                  value={applyForm.reason}
                  onChange={(e) => setApplyForm({ ...applyForm, reason: e.target.value })}
                  rows={3}
                  placeholder="Enter reason for leave..."
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  data-testid="reason-textarea"
                />
              </div>
            </div>
            <div className="p-4 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={() => setShowApplyModal(false)}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleApply}
                disabled={loading}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 flex items-center gap-2"
                data-testid="submit-leave-btn"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                Submit Application
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
